import { useState } from "react";
import { useLocation } from "react-router-dom";
import Drawer from "../../shared/Drawer";
import { useConfig } from "../../hooks";
import {
  useCancelAssistantActionMutation,
  useConfirmAssistantActionMutation,
  useSendAssistantMessageMutation,
} from "../../redux/services/assistant/assistantApi";
import AssistantLauncher from "./AssistantLauncher";
import AssistantPanel from "./AssistantPanel";
import { pageContextFromPath } from "./pageContext";

/**
 * Global host for the one assistant thread.
 * Mounted once on the dashboard so navigation keeps the same session.
 */
export default function AssistantHost() {
  const location = useLocation();
  const { appType } = useConfig();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [error, setError] = useState("");
  const [sendMessage, { isLoading: sending }] = useSendAssistantMessageMutation();
  const [confirmAction, { isLoading: confirming }] =
    useConfirmAssistantActionMutation();
  const [cancelAction, { isLoading: cancelling }] =
    useCancelAssistantActionMutation();

  const busy = sending || confirming || cancelling;

  const apply = (data) => {
    if (!data) return;
    if (data.sessionId) setSessionId(data.sessionId);
    if (Array.isArray(data.messages)) setMessages(data.messages);
    if (Array.isArray(data.pendingActions)) setPendingActions(data.pendingActions);
  };

  const onSend = async (message) => {
    setError("");
    try {
      const body = await sendMessage({
        sessionId: sessionId || undefined,
        message,
        context: pageContextFromPath(location.pathname),
      }).unwrap();
      apply(body?.data || body);
    } catch (err) {
      setError(err?.data?.message || "Assistant could not answer.");
    }
  };

  const onConfirm = async (action) => {
    setError("");
    try {
      const body = await confirmAction({
        actionId: action._id,
        sessionId,
      }).unwrap();
      apply(body?.data || body);
    } catch (err) {
      setError(err?.data?.message || "That change was not saved.");
      if (err?.data?.data) apply(err.data.data);
    }
  };

  const onCancel = async (action) => {
    setError("");
    try {
      const body = await cancelAction({
        actionId: action._id,
        sessionId,
      }).unwrap();
      apply(body?.data || body);
    } catch (err) {
      setError(err?.data?.message || "Could not cancel that action.");
    }
  };

  return (
    <>
      <AssistantLauncher open={open} onClick={() => setOpen((value) => !value)} />
      <Drawer
        title="Assistant"
        position="right"
        visible={open}
        setVisible={setOpen}
        widthClasses="w-[28rem] max-w-[100vw]"
      >
        <AssistantPanel
          appType={appType}
          messages={messages}
          pendingActions={pendingActions}
          sending={busy}
          error={error}
          onSend={onSend}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      </Drawer>
    </>
  );
}
